import * as THREE from 'three';
import { RoedenConfig } from '../types';
import { PROFILE } from '../utils/profileConstants';

interface BarPositions {
  verticals: number[];
  horizontals: number[];
}

function parsePattern(pattern: string): { cols: number; rows: number } | null {
  if (pattern === 'none') return null;
  if (pattern === 'perimeter') return null; // handled separately

  const match = pattern.match(/^(\d+)x(\d+)$/);
  if (match) {
    return { cols: parseInt(match[1], 10), rows: parseInt(match[2], 10) };
  }

  return null;
}

function computeBarPositions(
  clearWidth: number,
  clearHeight: number,
  cols: number,
  rows: number,
): BarPositions {
  const verticals: number[] = [];
  for (let i = 1; i <= cols; i++) {
    verticals.push((i / (cols + 1)) * clearWidth);
  }
  const horizontals: number[] = [];
  for (let i = 1; i <= rows; i++) {
    horizontals.push((i / (rows + 1)) * clearHeight);
  }
  return { verticals, horizontals };
}

/**
 * Create roeden (bar pattern) geometry for a glass pane.
 * Bars are positioned relative to the glass center.
 */
export function createRoedenPattern(
  clearWidth: number,
  clearHeight: number,
  config: RoedenConfig,
  material: THREE.Material,
): THREE.Group {
  const group = new THREE.Group();
  group.name = 'roeden';

  if (config.pattern === 'none' || config.mode === 'none') return group;

  // Z-offset based on mode
  let zOffset = 0;
  switch (config.mode) {
    case 'applied':
      zOffset = PROFILE.glassThickness / 2 + PROFILE.roedenBarDepth / 2;
      break;
    case 'between-glass':
      zOffset = 0;
      break;
    case 'true-divided':
      zOffset = 0;
      break;
  }

  const barW = PROFILE.roedenBarWidth;
  const barD = config.mode === 'between-glass'
    ? PROFILE.roedenBarWidth * 0.5
    : PROFILE.roedenBarDepth;

  if (config.pattern === 'perimeter') {
    createPerimeterBars(group, clearWidth, clearHeight, barW, barD, zOffset, material);
    return group;
  }

  // Custom NxM or preset patterns
  const parsed = parsePattern(config.pattern);
  if (!parsed && config.pattern === 'custom') {
    const cols = config.customCols ?? 1;
    const rows = config.customRows ?? 1;
    createGridBars(group, clearWidth, clearHeight, cols, rows, barW, barD, zOffset, material);
  } else if (parsed) {
    createGridBars(group, clearWidth, clearHeight, parsed.cols, parsed.rows, barW, barD, zOffset, material);
  }

  return group;
}

function createGridBars(
  group: THREE.Group,
  clearWidth: number,
  clearHeight: number,
  cols: number,
  rows: number,
  barW: number,
  barD: number,
  zOffset: number,
  material: THREE.Material,
): void {
  const positions = computeBarPositions(clearWidth, clearHeight, cols, rows);
  const halfW = clearWidth / 2;
  const halfH = clearHeight / 2;

  // Vertical bars (full height)
  for (const x of positions.verticals) {
    const geo = new THREE.BoxGeometry(barW, clearHeight, barD);
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.set(x - halfW, 0, zOffset);
    group.add(mesh);
  }

  // Horizontal bars (full width)
  for (const y of positions.horizontals) {
    const geo = new THREE.BoxGeometry(clearWidth, barW, barD);
    const mesh = new THREE.Mesh(geo, material);
    mesh.position.set(0, y - halfH, zOffset);
    group.add(mesh);
  }
}

function createPerimeterBars(
  group: THREE.Group,
  clearWidth: number,
  clearHeight: number,
  barW: number,
  barD: number,
  zOffset: number,
  material: THREE.Material,
): void {
  const inset = barW * 1.5;
  const innerW = clearWidth - 2 * inset;
  const innerH = clearHeight - 2 * inset;

  // Top bar
  const topGeo = new THREE.BoxGeometry(innerW, barW, barD);
  const top = new THREE.Mesh(topGeo, material);
  top.position.set(0, (clearHeight / 2) - inset, zOffset);
  group.add(top);

  // Bottom bar
  const bottomGeo = new THREE.BoxGeometry(innerW, barW, barD);
  const bottom = new THREE.Mesh(bottomGeo, material);
  bottom.position.set(0, -(clearHeight / 2) + inset, zOffset);
  group.add(bottom);

  // Left bar
  const leftGeo = new THREE.BoxGeometry(barW, innerH, barD);
  const left = new THREE.Mesh(leftGeo, material);
  left.position.set(-(clearWidth / 2) + inset, 0, zOffset);
  group.add(left);

  // Right bar
  const rightGeo = new THREE.BoxGeometry(barW, innerH, barD);
  const right = new THREE.Mesh(rightGeo, material);
  right.position.set((clearWidth / 2) - inset, 0, zOffset);
  group.add(right);
}
